import { useState, useEffect, useImperativeHandle, forwardRef } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material'
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore'
import { Link as RouterLink } from 'react-router-dom'
import PaymentIcon from '@mui/icons-material/Payment'
import PersonIcon from '@mui/icons-material/Person'
import { newFirestore } from '../../../../utils/firebase/firebase'

const RecentTransactions = forwardRef((props, ref) => {
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchRecentTransactions()
  }, [])

  useImperativeHandle(ref, () => ({
    refresh: fetchRecentTransactions,
  }))

  const fetchRecentTransactions = async () => {
    try {
      setLoading(true)
      const paymentsRef = collection(newFirestore, 'terminal_payments')
      const q = query(paymentsRef, orderBy('created', 'desc'), limit(10))
      const querySnapshot = await getDocs(q)

      const transactionsList = []
      querySnapshot.forEach((doc) => {
        const data = doc.data()
        transactionsList.push({
          id: doc.id,
          ...data,
          // Convert Firestore timestamp to Date
          created: data.created?.toDate() || new Date(),
        })
      })

      setTransactions(transactionsList)
      setError(null)
    } catch (err) {
      console.error('Error fetching transactions:', err)
      setError('Failed to load transactions')
    } finally {
      setLoading(false)
    }
  }

  const formatAmount = (amount, currency = 'gbp') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100) // Convert from pence to pounds
  }

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'success'
      case 'processing':
        return 'warning'
      case 'requires_payment_method':
        return 'error'
      default:
        return 'default'
    }
  }

  const isTestPayment = (transaction) => {
    // Check if payment is marked as test or if payment intent ID starts with test prefix
    return (
      transaction.isTest ||
      transaction.paymentIntentId?.startsWith('pi_test') ||
      process.env.NODE_ENV === 'development'
    )
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            Recent Transactions
          </Typography>
          <Box display='flex' justifyContent='center' py={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            Recent Transactions
          </Typography>
          <Alert severity='error'>{error}</Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent>
        <Typography variant='h6' gutterBottom>
          Recent Transactions
        </Typography>

        {transactions.length === 0 ? (
          <Box 
            sx={{ 
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant='body2' color='text.secondary' textAlign='center'>
              No transactions yet. Process your first payment to see transaction
              history.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              height: 400,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.4)',
                },
              },
            }}
          >
            <List sx={{ py: 0 }}>
              {transactions.map((transaction) => (
                <ListItem key={transaction.id} divider>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {transaction.user ? <PersonIcon /> : <PaymentIcon />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        display='flex'
                        alignItems='center'
                        gap={1}
                        flexWrap='wrap'
                      >
                        <Typography variant='body1' component='span'>
                          {formatAmount(transaction.amount, transaction.currency)}
                        </Typography>
                        <Chip
                          label={transaction.status}
                          color={getStatusColor(transaction.status)}
                          size='small'
                          sx={{ textTransform: 'uppercase' }}
                        />
                        {isTestPayment(transaction) && (
                          <Chip
                            label='TEST'
                            color='warning'
                            size='small'
                            variant='outlined'
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant='body2' color='text.secondary'>
                          {formatTime(transaction.created)}
                        </Typography>
                        {transaction.user && (
                          <Link
                            component={RouterLink}
                            to={`/admin/users/${transaction.user.id}`}
                            variant='body2'
                            color='primary.main'
                            underline='hover'
                            sx={{ display: 'block' }}
                          >
                            {transaction.user.firstName}{' '}
                            {transaction.user.lastName}
                          </Link>
                        )}
                        {transaction.description && (
                          <Typography variant='body2' color='text.secondary'>
                            {transaction.description}
                          </Typography>
                        )}
                        <Typography variant='caption' color='text.secondary'>
                          ID: {transaction.paymentIntentId.slice(-8)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  )
})

export default RecentTransactions
